'use client';
import {DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger} from "@/shadcn/components/ui/dropdown-menu";
import {Button} from "@/shadcn/components/ui/button";
import {LogOut, UserRound} from "lucide-react";
import Link from "next/link";
import {authClient} from "@/lib/auth-client";
import {useEffect, useState} from "react";
import {Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger} from "@/shadcn/components/ui/drawer";
import {useRouter} from "next/navigation";

const AuthDropdown = () => {
    const {refetch, data} = authClient.useSession();
    const router = useRouter();
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        // Check if we're on the client side
        if (typeof window !== 'undefined') {
            const checkMobile = () => {
                setIsMobile(window.innerWidth < 640);
            };

            // Initial check
            checkMobile();

            // Listen for window resize
            window.addEventListener('resize', checkMobile);

            // Clean up
            return () => window.removeEventListener('resize', checkMobile);
        }
    }, []);

    const handleLogOut = async () => {
        await authClient.signOut({
            fetchOptions: {
                onSuccess: () => {
                    // refresh() updates server components (AuthenticationButtonServer)
                    // User stays on current page
                    router.refresh();
                },
            },
        });
    };

    // @ts-expect-error - ignore this
    const userName = data?.user?.firstname ?? data?.user.name ?? data?.user?.username ?? 'Du';

    // Mobile drawer component
    const MobileDrawer = () => (
        <Drawer>
            <DrawerTrigger asChild>
                <Button
                    variant={'outline'}
                    size={'icon'}
                    className={'relative flex-col items-center justify-center focus:ring-0 gap-0.5 -mt-4'}>
                    <UserRound className={"h-5 w-5"}/>
                    <span className={"absolute mt-14 text-[0.65rem]"}>Konto</span>
                </Button>
            </DrawerTrigger>
            <DrawerContent>
                <DrawerHeader>
                    <DrawerTitle className="flex items-center">
                        <UserRound className="h-5 w-5 mr-2"/>
                        Hi, {userName}
                    </DrawerTitle>
                </DrawerHeader>
                <div className="px-4 py-4 space-y-4">
                    <DrawerClose asChild>
                        <Link href={'/konto'} className="flex w-full p-2 rounded-md hover:bg-gray-100" prefetch={false}>
                            <span>Konto</span>
                        </Link>
                    </DrawerClose>
                    <DrawerClose asChild>
                        <Link href={'/wunschlisten'} className="flex w-full p-2 rounded-md hover:bg-gray-100" prefetch={false}>
                            <span>Wunschliste</span>
                        </Link>
                    </DrawerClose>
                    <div className="border-t border-slate-200 pt-4">
                        <DrawerClose asChild>
                            <Button
                                variant="ghost"
                                className="w-full justify-start"
                                onClick={handleLogOut}
                            >
                                <LogOut className="h-4 w-4 mr-2"/>
                                <span>Log out</span>
                            </Button>
                        </DrawerClose>
                    </div>
                </div>
            </DrawerContent>
        </Drawer>
    );

    // Desktop dropdown component
    const DesktopDropdown = () => (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant={'outline'}
                    className={'flex relative items-center justify-center focus:ring-0 gap-2'}>
                    <UserRound className={"h-5 w-5"}/>
                    <span className={"text-xs lg:text-sm"}>Hi, {userName}</span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
                <DropdownMenuGroup>
                    <DropdownMenuItem asChild>
                        <Link href={'/konto'} className={'w-full cursor-pointer'} prefetch={false}>
                            <span>Konto</span>
                        </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                        <Link href={'/wunschlisten'} className={'w-full cursor-pointer'} prefetch={false}>
                            <span>Wunschliste</span>
                        </Link>
                    </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator/>
                <DropdownMenuItem onClick={handleLogOut} className={'cursor-pointer'}>
                    <LogOut className={'h-4 w-4 mr-2'}/>
                    <span>Log out</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );

    return (
        <div className="md:flex">
            {isMobile ? <MobileDrawer/> : <DesktopDropdown/>}
        </div>
    );
};

export default AuthDropdown;
